import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CircularProgress, createTheme, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DrawerContext } from "../../Menu/responsive-drawer-context";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { Timeframe } from "../../../features/FloorManager/typings/shared/timeframe";
import { setMarkers, setSelectedLocation } from "../../../features/Reports/slices/report.slice";
import { fetchMarkers } from "../../../features/Reports/thunks/report.thunk";
import {
  FloorPlanMemo,
  MapMarkerLocation,
  TimeframeString
} from "../../../features/Reports/typings/reports.types";
import { getColor } from "./area-chart.component";
import FacilityReportGoogleMap from "./facility-report-google-map.component";
import FacilityReportRestrictionPicker from "./facility-report-restriction-picker.component";
import FacilityReportDialog from "./facility-report-dialog.component";
import {
  FACILITY_REPORT_LOCATION_AUTOCOMPLETE_EXTRA_LEFT_PX,
  FACILITY_REPORT_LOCATION_AUTOCOMPLETE_MAP_CONTROLS_GAP_PX,
  getFacilityReportLocationAutocompleteLeftPx,
  getFacilityReportLocationAutocompleteTopPx,
  getGoogleMapFullscreenPortalRoot,
  getGoogleMapTopLeftControlsLayoutPx,
  getLocationOverlapMetadata,
  getMapLocationAutocompleteLeftDesktopPx
} from "./functions/facility-report.functions";
import { FacilityReportMapLocationAutocomplete } from "./facility-report-map-location-autocomplete.component";

// --- Types -----------------------------------------------------------------

type LocationReportProps = {
  floorData: FloorPlanMemo;
  memoizeFloorPlan: (floorInventoryId: number, date: Timeframe) => void;
};

// --- Pure helpers (exported for tests) ------------------------------------

export const checkCurrentFloorAndTime = (
  currentFloors: Array<{ id: number; open: boolean }>,
  selectedTimeframe: TimeframeString | undefined
) => {
  return currentFloors.length && selectedTimeframe?.timeframeStart && currentFloors[0].id !== -1;
};

// --- Facility report (map + floor plans) -----------------------------------

/**
 * Facility manager capacity report: map, floor plan dialog, restriction picker, and map chrome.
 */
const LocationReport: React.FC<LocationReportProps> = ({ memoizeFloorPlan, floorData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const { isOpen: isDesktopDrawerExpanded } = useContext(DrawerContext);

  const {
    userRoles: { isCapacityResponsible },
    userInformation: { sub }
  } = useSelector((state: RootState) => state.login);
  const { mapMarkerLocations, selectedTimeframe, capacitySelectedFloors, currentFloors } =
    useSelector((state: RootState) => state.report);

  const facilityReportBreakpointProbeTheme = useMemo(
    () =>
      createTheme({
        breakpoints: {
          values: {
            xs: 400,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536
          }
        }
      }),
    []
  );

  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"), { noSsr: true });
  const isSmallPhone = useMediaQuery(facilityReportBreakpointProbeTheme.breakpoints.down("xs"), {
    noSsr: true
  });

  const [mapTopControlsRightPx, setMapTopControlsRightPx] = useState<number | null>(null);
  const [zoneBookingReport, setZoneBookingReport] = useState(!isCapacityResponsible);
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const [selectedLocationIdToFocus, setSelectedLocationIdToFocus] = useState<number | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapFullscreenPortalRoot, setMapFullscreenPortalRoot] = useState<Element | null>(null);

  const colorScalePos = isMobile ? 158 : 200;

  const locationAutocompleteLeftDesktop = useMemo(
    () =>
      getMapLocationAutocompleteLeftDesktopPx({
        isDesktopDrawerExpanded,
        spacing7: muiTheme.spacing(7),
        spacing2: muiTheme.spacing(2),
        extraOffsetPx: FACILITY_REPORT_LOCATION_AUTOCOMPLETE_EXTRA_LEFT_PX
      }),
    [isDesktopDrawerExpanded, muiTheme]
  );

  const locationAutocompleteFallbackLeftPx = isMobile ? 10 : locationAutocompleteLeftDesktop;

  const locationAutocompleteLeftPx = useMemo(
    () =>
      getFacilityReportLocationAutocompleteLeftPx({
        mapTopControlsRightPx,
        controlsGapPx: FACILITY_REPORT_LOCATION_AUTOCOMPLETE_MAP_CONTROLS_GAP_PX,
        normalFallbackLeftPx: locationAutocompleteFallbackLeftPx,
        isMobile,
        isMapBrowserFullscreen: mapFullscreenPortalRoot != null
      }),
    [isMobile, locationAutocompleteFallbackLeftPx, mapFullscreenPortalRoot, mapTopControlsRightPx]
  );

  const locationAutocompleteTopCss = useMemo(() => {
    const fallbackTopPx = getFacilityReportLocationAutocompleteTopPx({
      isMobile,
      isMapBrowserFullscreen: mapFullscreenPortalRoot != null
    });

    if (isMobile) {
      return `calc(env(safe-area-inset-top, 0px) + ${fallbackTopPx}px)`;
    }

    return `${fallbackTopPx}px`;
  }, [isMobile, mapFullscreenPortalRoot]);

  const overlapMetadata = useMemo(
    () => getLocationOverlapMetadata(mapMarkerLocations),
    [mapMarkerLocations]
  );

  const locationOptions = useMemo(
    () =>
      mapMarkerLocations?.filter(
        location =>
          location.geoInformation?.[0]?.latitude != null &&
          location.geoInformation?.[0]?.longitude != null
      ) ?? [],
    [mapMarkerLocations]
  );

  const isOverlapDataReady = Object.keys(overlapMetadata).length === mapMarkerLocations?.length;

  const isLocationAutocompleteEnabled = !!locationOptions.length && isOverlapDataReady;

  const selectedLocationOption =
    locationOptions.find(location => location.locationInventoryId === selectedLocationIdToFocus) ??
    null;

  const colorScale = useMemo(
    () => [
      { percent: "20%", color: getColor(0.2) },
      { percent: "40%", color: getColor(0.4) },
      { percent: "60%", color: getColor(0.6) },
      { percent: "80%", color: getColor(0.8) },
      { percent: "100%", color: getColor(1) }
    ],
    []
  );

  const checkCurrentFloorsFloorData =
    currentFloors.filter(floor => floorData[floor.id]).length !== currentFloors.length;

  const memoized = (i: number) => {
    memoizeFloorPlan(currentFloors[i].id, {
      start: new Date(selectedTimeframe?.timeframeStart as string).toISOString(),
      end: new Date(selectedTimeframe?.timeframeEnd as string).toISOString()
    });
  };

  const handleClearFacilityReportLocation = useCallback(() => {
    setSelectedLocationIdToFocus(null);
  }, []);

  const handlePickFacilityReportLocation = useCallback(
    (location: MapMarkerLocation) => {
      setSelectedLocationIdToFocus(location.locationInventoryId);
      dispatch(
        setSelectedLocation({
          id: location.locationInventoryId,
          open: false,
          sub
        })
      );
    },
    [dispatch, sub]
  );

  const facilityLocationAutocomplete = useMemo(
    () => (
      <FacilityReportMapLocationAutocomplete
        mapFullscreenPortalRoot={mapFullscreenPortalRoot}
        isMobile={isMobile}
        muiTheme={muiTheme}
        t={t}
        selectedLocationOption={selectedLocationOption}
        locationOptions={locationOptions}
        isLocationAutocompleteEnabled={isLocationAutocompleteEnabled}
        onClearSelection={handleClearFacilityReportLocation}
        onPickLocation={handlePickFacilityReportLocation}
        locationAutocompleteTopCss={locationAutocompleteTopCss}
        locationAutocompleteLeftPx={locationAutocompleteLeftPx}
      />
    ),
    [
      handleClearFacilityReportLocation,
      handlePickFacilityReportLocation,
      isLocationAutocompleteEnabled,
      isMobile,
      locationAutocompleteLeftPx,
      locationAutocompleteTopCss,
      locationOptions,
      mapFullscreenPortalRoot,
      muiTheme,
      selectedLocationOption,
      t
    ]
  );

  useEffect(() => {
    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;

    const syncMapChromeLayout = () => {
      const cameraControl = document.querySelector(
        "gmp-internal-camera-control button[aria-controls]"
      );

      if (cameraControl) {
        const isExpanded = cameraControl.getAttribute("aria-expanded") === "true";
        setControlsExpanded(isExpanded);
      }

      const layout = getGoogleMapTopLeftControlsLayoutPx();
      setMapTopControlsRightPx(layout.right);
    };

    const attachWhenMapReady = () => {
      if (cancelled) {
        return;
      }

      const mapContainer = document.querySelector(".gm-style");

      if (!mapContainer) {
        if (retryTimeoutId != null) {
          clearTimeout(retryTimeoutId);
        }
        retryTimeoutId = setTimeout(attachWhenMapReady, 100);
        return;
      }

      if (retryTimeoutId != null) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }

      if (observer) {
        observer.disconnect();
      }

      observer = new MutationObserver(syncMapChromeLayout);
      observer.observe(mapContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "aria-expanded"]
      });

      globalThis.addEventListener("resize", syncMapChromeLayout);
      syncMapChromeLayout();
    };

    attachWhenMapReady();

    return () => {
      cancelled = true;
      if (retryTimeoutId != null) {
        clearTimeout(retryTimeoutId);
      }

      globalThis.removeEventListener("resize", syncMapChromeLayout);
      observer?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!isMapReady) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const layout = getGoogleMapTopLeftControlsLayoutPx();
      setMapTopControlsRightPx(layout.right);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [isMapReady]);

  useEffect(() => {
    const syncMapFullscreenPortal = () => {
      setMapFullscreenPortalRoot(getGoogleMapFullscreenPortalRoot(document));
    };

    syncMapFullscreenPortal();
    document.addEventListener("fullscreenchange", syncMapFullscreenPortal);
    document.addEventListener("webkitfullscreenchange", syncMapFullscreenPortal);
    document.addEventListener("mozfullscreenchange", syncMapFullscreenPortal);
    document.addEventListener("MSFullscreenChange", syncMapFullscreenPortal);

    return () => {
      document.removeEventListener("fullscreenchange", syncMapFullscreenPortal);
      document.removeEventListener("webkitfullscreenchange", syncMapFullscreenPortal);
      document.removeEventListener("mozfullscreenchange", syncMapFullscreenPortal);
      document.removeEventListener("MSFullscreenChange", syncMapFullscreenPortal);
    };
  }, []);

  useEffect(() => {
    if (checkCurrentFloorAndTime(currentFloors, selectedTimeframe) && checkCurrentFloorsFloorData) {
      currentFloors.forEach((floor, i) => {
        if (!floorData[floor.id]) {
          memoized(i);
        }
      });
    }
  }, [capacitySelectedFloors, currentFloors, memoizeFloorPlan]);

  useEffect(() => {
    if (checkCurrentFloorAndTime(currentFloors, selectedTimeframe)) {
      currentFloors.forEach((floor, i) => {
        memoized(i);
      });
    }
  }, [selectedTimeframe?.timeframeEnd, selectedTimeframe?.timeframeStart]);

  useEffect(() => {
    if (!mapMarkerLocations && !zoneBookingReport) {
      dispatch(fetchMarkers({ status: true, mode: "capacity" }));
    }
  }, [dispatch, isCapacityResponsible, mapMarkerLocations, zoneBookingReport]);

  useEffect(() => {
    return () => {
      dispatch(setMarkers(undefined));
    };
  }, [dispatch]);

  return (
    <div style={{ height: "calc(100vh - 70px)" }}>
      <div style={{ width: "auto", height: "100%", padding: 0 }}>
        {!mapMarkerLocations && (
          <div
            style={{
              width: "auto",
              height: "100%",
              padding: 0,
              background: "black",
              opacity: 0.2,
              zIndex: 999
            }}
          >
            <CircularProgress
              sx={{
                position: "fixed",
                bottom: "45%",
                right: "35%",
                float: "right",
                color: "white",
                display: "flex"
              }}
            />
            {zoneBookingReport && (
              <Typography
                sx={{
                  position: "fixed",
                  bottom: "40%",
                  right: "30%",
                  float: "right",
                  color: "white",
                  display: "flex"
                }}
              >
                Please select restriction
              </Typography>
            )}
          </div>
        )}

        <FacilityReportGoogleMap
          zoneBookingReport={zoneBookingReport}
          focusedLocationId={selectedLocationIdToFocus}
          onMapReadyChange={setIsMapReady}
        />
      </div>

      <div
        data-testid="color-scale-container"
        style={{
          position: "fixed",
          bottom: isMobile ? 37 : 29,
          right: controlsExpanded ? colorScalePos : 70,
          float: "right",
          display: "flex"
        }}
      >
        {colorScale.map((color, index) => (
          <div
            key={color.color}
            data-testid={`color-item-${index}`}
            style={{
              background: color.color,
              width: isSmallPhone && controlsExpanded ? 35 : 45,
              border: "2px solid white",
              borderRadius: 4,
              margin: "0 2px 0 2px",
              height: isSmallPhone && controlsExpanded ? 30 : 40,
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: isSmallPhone && controlsExpanded ? "0.75rem" : "1rem"
            }}
          >
            {color.percent}
          </div>
        ))}
      </div>

      <FacilityReportDialog zoneBookingReport={zoneBookingReport} floorData={floorData} />

      <FacilityReportRestrictionPicker
        zoneBookingReport={zoneBookingReport}
        setZoneBookingReport={setZoneBookingReport}
      />

      {isMapReady &&
        (mapFullscreenPortalRoot
          ? createPortal(facilityLocationAutocomplete, mapFullscreenPortalRoot)
          : facilityLocationAutocomplete)}
    </div>
  );
};

export default LocationReport;
